<template>
  <v-container>
    <page-heading style="margin-bottom: 0 !important;">{{ pageTitle }}</page-heading>

    <div class="d-flex justify-end mb-3">
      <v-btn
        @click.native="pushToRoute('SurveyQuestions')"
        v-if="hasAnyPermission('SURVEY_EDIT')"
      >
        <v-icon left>mdi-pencil</v-icon>
        {{ trans('survey.edit') }}
      </v-btn>
    </div>

    <ajax-table
      ref="surveysTable"
      :resource="resourceUrl"
      :filter="filter"
      :headers="[
        {text: 'booking.bookingId', value: 'id'},
        {text: 'object.object', value: 'objectName'},
        {text: 'survey.status', value: 'surveyStatus'},
        {text: 'survey.lastActionAt', value: 'surveyResult.updatedAt'},
        {text: 'survey.hasFilledTextAnswer', value: 'hasFilledTextAnswer', sortable: false},
        {text: 'booking.checkinAt', value: 'checkinAt'},
        {text: 'booking.checkoutAt', value: 'checkoutAt'},
        {text: 'guest.guest', value: 'guestName'},
        {text: '', value: 'actions'},
      ]"
      :page-options="{itemsPerPage: 5, sortBy: ['surveyResult.updatedAt'], sortDesc: [true]}"
      :items-per-page-options="[5, 10, 20]"
    >

      <template v-slot:filters>
        <v-col cols="6" md="6" lg="3">
          <v-select
            v-model="filterData.status"
            :label="trans('survey.filterByStatus')"
            :items="allSurveyStatusList"
            clearable
            hide-details
            style="max-width: 300px;"
          >

            <template v-slot:selection="{ item }">
              <survey-status :booking="allSurveyStatus[item].booking" full-text/>
            </template>

            <template v-slot:item="{ item }">
              <survey-status :booking="allSurveyStatus[item].booking" full-text/>
            </template>

          </v-select>
        </v-col>

        <v-col cols="6" md="6" lg="3">
          <v-checkbox
            hide-details
            v-model="filterData.showDisabled"
            :label="trans('survey.filterShowDisabled')"
            v-show="!filterData.status || filterData.status === 'NOT_NEEDED'"
          />
        </v-col>

        <v-col cols="6" md="6" lg="3">
          <date-select
            v-model="filterData.checkoutBefore"
            :label="trans('survey.filterCheckoutBefore')"
          />
        </v-col>

        <v-col cols="6" md="6" lg="3">
          <search v-model="filterData.search" />
        </v-col>

      </template>

      <template v-slot:item.id="{ item }">
        <booking-id
          :value="item"
          :open-in-new-tab="false"
          :custom-link="{name: 'SurveyResultPage', params: {bookingId: item.id}}"
        />
      </template>

      <template v-slot:item.objectName="{ item }">
        <object-name :value="item" />
      </template>

      <template v-slot:item.surveyStatus="{ item }">
        <survey-status :booking="item" :full-text="true"/>
      </template>

      <template v-slot:item.hasFilledTextAnswer="{ item }">
        <v-icon v-if="item.surveyResult && item.surveyResult.hasFilledTextAnswer" :title="trans('survey.hasFilledTextAnswerTitle')">mdi-message-reply-text</v-icon>
      </template>

      <template v-slot:item.surveyResult.updatedAt="{ item }">
        <span v-if="item.surveyResult" :title="formatDate(item.surveyResult.updatedAt, 'LLL')">{{ formatDate(item.surveyResult.updatedAt, 'fromNow') }}</span>
      </template>

      <template v-slot:item.guestName="{ item }">
        <guest-name :value="item"/>
      </template>

      <template v-slot:item.actions="{ item }">
        <context-menu-icon>
          <v-list dense>
            <v-list-item
              v-if="item.surveyResult != null && item.surveyResult.answeredAt != null"
              @click="pushToRoute('SurveyResultPage', {bookingId: item.id})"
            >
              <v-list-item-icon><v-icon>mdi-clipboard-text-search</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.viewSurveyResult') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canSurveyResultBeUpdated(item.surveyResult) && item.surveyResult.status === 'ANSWERED'"
              @click="toggleSurveyReviewedAndReload(item)"
            >
              <v-list-item-icon><v-icon>mdi-clipboard-check</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.setReviewed') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canSurveyResultBeUpdated(item.surveyResult) && item.surveyResult.status === 'REVIEWED'"
              @click="toggleSurveyReviewedAndReload(item)"
            >
              <v-list-item-icon><v-icon>mdi-clipboard-off</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.setNotReviewed') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="item.surveyResult != null && item.surveyResult.lastEmail != null"
              @click="getEmail(item.surveyResult.lastEmail)"
            >
              <v-list-item-icon><v-icon>mdi-email-search</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.showEmail') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canSurveyEmailBeSent(item)"
              @click="sendSurveyMailAndReload(item)"
            >
              <v-list-item-icon><v-icon>mdi-email-send</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.sendEmail') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canAutoSurveyBeDeactivated(item)"
              @click="toggleBookingSurveyStatusAndReload(item)"
            >
              <v-list-item-icon><v-icon>mdi-email-off</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.disableSending') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canAutoSurveyBeActivated(item)"
              @click="toggleBookingSurveyStatusAndReload(item)"
            >
              <v-list-item-icon><v-icon>mdi-email-plus</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.enableSending') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="pushToRoute('BookingDetails', {bookingId: item.id})"
            >
              <v-list-item-icon><v-icon>mdi-book-outline</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('survey.showBooking') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </context-menu-icon>
      </template>

    </ajax-table>

    <request-state
      :request-promise="surveyStatisticsRequest"
      :retry-action="loadStatistics"
      class="mt-16"
    >
      <div class="text-h4 mb-0">Statistik</div>
      <div class="d-flex mb-4">
        <v-spacer></v-spacer>
        <select-object
          :object-id.sync="surveyStatisticsFilterData.objectId"
          :label="trans('general.filterBy', {property: transSingular('object.object')})"
          clearable hide-details
          style="max-width: 300px;"
        />
      </div>
      <template v-if="surveyStatistics">
        <survey-statistics-summary-card
          :survey-statistics-summary="surveyStatistics.summary"
        />
        <survey-question-card
          v-for="(surveyQuestionStatistic, i) in surveyQuestionStatistics"
          :key="'surveyQuestionStatistic-'+i"
          class="mt-8"
          :survey-question-statistic="surveyQuestionStatistic"
        />
      </template>
    </request-state>

    <validation-errors-modal
      :title="trans('error.general')"
      :show-modal.sync="showSurveyEmailErrorModal"
      :validation-errors.sync="surveyEmailValidationErrors"
    />

    <email-modal
      :show-modal.sync="showEmailModal"
      :email="currentEmail"
    />

  </v-container>
</template>

<script>
import {LocaleUtils} from '../utils/locale-utils';
import PageHeading from '../components/PageHeading';
import {DateUtils} from '../utils/date-utils';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '@/utils/main-utils';
import AjaxTable from '@/components/AjaxTable';
import SurveyStatus from '@/components/SurveyStatus';
import {SurveyApi} from '@/api/survey-api';
import ContextMenuIcon from '@/components/ContextMenuIcon';
import {comparison, eq, lt, ne, or} from 'rsql-builder';
import GuestName from "@/components/GuestName";
import {SurveyUtils} from '../utils/survey-utils';
import {EmailUtils} from '../utils/email-utils';
import EmailModal from '../components/EmailModal';
import {FilterUtils} from '../utils/filter-utils';
import Search from '../components/Search';
import SurveyStatisticsSummaryCard from '@/components/survey/statistic/SurveyStatisticsSummaryCard';
import RequestState from '@/components/RequestState';
import {isNotNull, isNull} from '../lib/custom-rsql-operators';
import DateSelect from '../components/DateSelect';
import dayjs from 'dayjs';
import SurveyQuestionCard from '@/components/survey/statistic/SurveyQuestionCard';
import SelectObject from '@/components/SelectObject';
import ObjectName from '@/components/ObjectName';
import ValidationErrorsModal from '../components/ValidationErrorsModal';
import BookingId from '@/components/BookingId';

export default {
  name: 'SurveysPage',
  mixins: [
    LocaleUtils,
    DateUtils,
    RouterUtils,
    SurveyUtils,
    EmailUtils,
    FilterUtils,
    MainUtils
  ],
  components: {
    BookingId,
    ValidationErrorsModal,
    ObjectName,
    DateSelect,
    SelectObject,
    SurveyQuestionCard,
    Search,
    RequestState,
    SurveyStatisticsSummaryCard,
    EmailModal,
    ContextMenuIcon,
    SurveyStatus,
    AjaxTable,
    PageHeading,
    GuestName,
  },
  data: () => ({
    filterData: {
      search: null,
      showDisabled: false,
      checkoutBefore: dayjs().format('YYYY-MM-DD'),
      status: null
    },
    allSurveyStatus: {
      'NOT_NEEDED': {booking: {surveyStatus: 'NOT_NEEDED'}, summaryAttribute: 'disabled'},
      'MISSING': {booking: {surveyStatus: 'NEEDED', surveyResult: null}, summaryAttribute: 'missing'},
      'SENT': {booking: {surveyResult: {status: 'SENT'}}, summaryAttribute: 'sent'},
      'OPENED': {booking: {surveyResult: {status: 'OPENED'}}, summaryAttribute: 'opened'},
      'ANSWERED': {booking: {surveyResult: {status: 'ANSWERED'}}, summaryAttribute: 'answered'},
      'REVIEWED': {booking: {surveyResult: {status: 'REVIEWED'}}, summaryAttribute: 'reviewed'},
    },
    showSurveyEmailErrorModal: false,
    surveyEmailValidationErrors: null,
    /** @type SurveyResultStatisticDto */
    surveyStatistics: null,
    surveyStatisticsRequest: null,
    surveyStatisticsFilterData: {
      objectId: null
    }
  }),
  computed: {
    allSurveyStatusList () {
      return Object.keys(this.allSurveyStatus);
    },
    filter () {
      let filters =  [];
      if(this.filterData.search) {
        filters.push(this.buildSearchFilter(this.filterData.search, ['id', 'guest.lastName', 'guest.firstName', 'object.name']));
      }
      if(this.filterData.showDisabled === false) {
        filters.push(or(comparison('surveyStatus', eq('NEEDED')), comparison('surveyResult.id', isNotNull())));
      }
      if(this.filterData.checkoutBefore != null) {
        filters.push(comparison('checkoutAt', lt(this.filterData.checkoutBefore)));
      }
      if(this.filterData.status != null) {
        /** {BookingSurveyDto} */
        const statusBooking = this.allSurveyStatus[this.filterData.status].booking;
        if(statusBooking.surveyStatus != null) {
          filters.push(comparison('surveyStatus', eq(statusBooking.surveyStatus)));
        }
        if(statusBooking.surveyResult == null) {
          filters.push(or(comparison('surveyResult.id', isNull()), comparison('surveyResult.lastEmail.status', ne('SENT'))));
        } else {
          switch (statusBooking.surveyResult.status) {
            case 'SENT':
              filters.push(comparison('surveyResult.lastEmail.status', eq('SENT')));
              filters.push(comparison('surveyResult.firstOpenedAt', isNull()));
              filters.push(comparison('surveyResult.answeredAt', isNull()));
              filters.push(comparison('surveyResult.reviewedAt', isNull()));
              break;
            case 'OPENED':
              filters.push(comparison('surveyResult.firstOpenedAt', isNotNull()));
              filters.push(comparison('surveyResult.answeredAt', isNull()));
              filters.push(comparison('surveyResult.reviewedAt', isNull()));
              break;
            case 'ANSWERED':
              filters.push(comparison('surveyResult.firstOpenedAt', isNotNull()));
              filters.push(comparison('surveyResult.answeredAt', isNotNull()));
              filters.push(comparison('surveyResult.reviewedAt', isNull()));
              break;
            case 'REVIEWED':
              filters.push(comparison('surveyResult.firstOpenedAt', isNotNull()));
              filters.push(comparison('surveyResult.answeredAt', isNotNull()));
              filters.push(comparison('surveyResult.reviewedAt', isNotNull()));
              break;
          }

        }
      }
      return filters;
    },
    resourceUrl () {
      return SurveyApi.apiUri;
    },
    pageTitle () {
      return this.trans('survey.surveys');
    },
    surveyQuestionStatistics () {
      return this.surveyStatistics?.surveyQuestions;
    }
  },
  mounted() {
    this.loadStatistics();
  },
  methods: {
    loadStatistics() {
      this.surveyStatisticsRequest = SurveyApi.getSurveyStatistic({objectId: this.surveyStatisticsFilterData.objectId});
      this.surveyStatisticsRequest.then(response => {
        this.surveyStatistics = response.body;
        this.surveyStatistics.surveyQuestions.sort((a, b) => a.surveyQuestion.order - b.surveyQuestion.order);
      });
    },
    sendSurveyMailAndReload (booking) {
      this.sendSurveyMail(booking)
        .then(() => {
          this.$refs.surveysTable.loadData();
          this.loadStatistics();
        })
        .catch(error => {
          this.surveyEmailValidationErrors = this.getValidationErrors(error, false);
          this.showSurveyEmailErrorModal = true;
        })
      ;
    },
    toggleBookingSurveyStatusAndReload (booking) {
      this.toggleBookingSurveyStatus(booking)
        .then(() => {
          this.$refs.surveysTable.loadData();
          this.loadStatistics();
        });
    },

    toggleSurveyReviewedAndReload (booking) {
      if (!booking?.surveyResult) {
        return;
      }
      this.toggleSurveyReviewed(booking.surveyResult)
        .then(() => {
          this.$refs.surveysTable.loadData();
          this.loadStatistics();
        });
    }
  },
  watch: {
    filterData: {
      deep: true,
      immediate: true,
      handler () {
        if (this.filterData.status === 'NOT_NEEDED') {
          this.filterData.showDisabled = true;
        }
      }
    },
    surveyStatisticsFilterData: {
      deep: true,
      handler () {
        this.loadStatistics();
      }
    }
  }
}
</script>

<style>
</style>
